/**
 * The caller's simulator runs (newest first): status, live progress, headline, delete.
 */
import React from 'react';
import { Box, Card, CardContent, Chip, Divider, IconButton, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { SimRunRow } from '../../services/optionsim.service';
import { dateTimeUtc } from './simFormat';

export interface LiveProgress { id: number; pct: number; phase?: string | null }

const statusColor = (s: string): 'success' | 'info' | 'error' | 'warning' | 'default' =>
  s === 'DONE' ? 'success' : s === 'RUNNING' || s === 'QUEUED' ? 'info' : s === 'FAILED' ? 'error' : s === 'CANCELLED' ? 'warning' : 'default';

export const SimRunsList: React.FC<{
  runs: SimRunRow[]; selectedId: number | null; live: LiveProgress | null;
  onOpen: (id: number) => void; onDelete: (id: number) => void; onRefresh: () => void;
}> = ({ runs, selectedId, live, onOpen, onDelete, onRefresh }) => (
  <Card data-testid="optionsim-runs">
    <CardContent sx={{ pb: '8px !important' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={800}>Simulations</Typography>
        <Tooltip title="Refresh" arrow><IconButton size="small" onClick={onRefresh} aria-label="Refresh runs"><RefreshIcon fontSize="small" /></IconButton></Tooltip>
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Stack spacing={0.5} sx={{ maxHeight: 460, overflowY: 'auto' }}>
        {runs.map(r => {
          const inFlight = r.status === 'RUNNING' || r.status === 'QUEUED';
          const pct = live && live.id === r.id ? live.pct : r.progressPct;
          const best = r.headline?.best as string | null | undefined;
          return (
            <Box key={r.id} onClick={() => onOpen(r.id)} data-testid={`optionsim-run-${r.id}`}
              sx={{ p: 1, borderRadius: 1, cursor: 'pointer', border: '1px solid', borderColor: selectedId === r.id ? 'primary.main' : 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                <Typography variant="caption" fontWeight={700} sx={{ minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.35 }}>{r.name || `Run ${r.id}`}</Typography>
                <Chip size="small" label={r.status} color={statusColor(r.status)} sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0 }} />
              </Stack>
              {inFlight && <LinearProgress variant={r.status === 'QUEUED' ? 'indeterminate' : 'determinate'} value={pct} sx={{ mt: 0.5, height: 4, borderRadius: 1 }} />}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                  {dateTimeUtc(new Date(r.createdAt).getTime() / 1000)} UTC{best && r.status === 'DONE' ? ` · best: ${best}` : ''}
                </Typography>
                <Tooltip title={inFlight ? 'Cancel and delete' : 'Delete'} arrow>
                  <IconButton size="small" aria-label="Delete run" onClick={e => { e.stopPropagation(); onDelete(r.id); }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton>
                </Tooltip>
              </Stack>
            </Box>
          );
        })}
        {!runs.length && <Typography variant="caption" color="text.secondary">No simulations yet. Pick strategies above and press Run.</Typography>}
      </Stack>
    </CardContent>
  </Card>
);

export default SimRunsList;
